import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { Redirect, Route, Switch } from "react-router-dom";
import { StyledPageWrapper, Title } from "@monorepo/components";
import {
  getRecurrentPaymentRoute,
  getTemplateServiceRoute,
} from "@monorepo/components/src/Payments/TemplateForm/useFindServiceProvider";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { lazyWithRetry } from "@monorepo/helpers";
import { setActiveCart } from "@monorepo/slices/activeCart";
import { IActiveCartSliceState } from "@monorepo/types";
import { Box, Button, styled } from "@mui/material";
import { reverse } from "named-urls";

import { useAppSelector } from "hooks";

const CartGroup = lazyWithRetry(() => import("@monorepo/components/src/Cart/CartGroup/CartGroup"));

const CartGroupOverview = lazyWithRetry(
  () => import("@monorepo/components/src/Cart/CartGroupOverview/CartGroupOverview")
);

const CreateCartGroup = lazyWithRetry(() => import("@monorepo/components/src/Cart/CreateCartGroup/CreateCartGroup"));

const EditCartGroup = lazyWithRetry(() => import("@monorepo/components/src/Cart/EditCartGroup/EditCartGroup"));

const EditSharedCartGroup = lazyWithRetry(
  () => import("@monorepo/components/src/Cart/EditSharedCartGroup/EditSharedCartGroup")
);

const EditSharedCartGroupSuccess = lazyWithRetry(
  () => import("@monorepo/components/src/Cart/EditSharedCartGroupSuccess/EditSharedCartGroupSuccess")
);

const ShareCartGroup = lazyWithRetry(() => import("@monorepo/components/src/Cart/ShareCartGroup/ShareCartGroup"));

const ShareCartGroupSuccess = lazyWithRetry(
  () => import("@monorepo/components/src/Cart/ShareCartGroupSuccess/ShareCartGroupSuccess")
);

const CartGroupSharedClients = lazyWithRetry(
  () => import("@monorepo/components/src/Cart/CartGroupSharedClients/CartGroupSharedClients")
);

const EditCart = lazyWithRetry(() => import("@monorepo/components/src/Cart/EditCart/EditCart"));

const ServiceListAutocomplete = lazyWithRetry(
  () => import("../components/ServiceListAutocomplete/ServiceListAutocomplete")
);

const ServiceForm = lazyWithRetry(() => import("../components/ServiceForms/ServiceForm"));

const ServiceRepeatPaymentForm = lazyWithRetry(
  () => import("@monorepo/components/src/ServiceForms/ServiceRepeatPaymentForm")
);

const PaymentOverview = lazyWithRetry(
  () => import("@monorepo/components/src/Payments/PaymentOverview/PaymentOverview")
);

const CashPayment = lazyWithRetry(() => import("@monorepo/components/src/Payments/CashPayment/CashPayment"));

const BankPayment = lazyWithRetry(() => import("@monorepo/components/src/Payments/BankPayment/BankPayment"));

const OrderProcessing = lazyWithRetry(
  () => import("@monorepo/components/src/Payments/OrderProcessing/OrderProcessing")
);

interface IPaymentsProps {
  cartGroupId?: string;
  clientId?: string;
  serviceId?: string;
  cartId?: string;
  orderId?: string;
  ignoreGroup: boolean;
  providers?: string;
  templateId?: string;
  isPaymentBankOnly: boolean;
}

const FormWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
}));
export const Payments: React.FC<IPaymentsProps> = ({
  cartGroupId,
  clientId,
  serviceId,
  cartId,
  orderId,
  ignoreGroup,
  providers,
  templateId,
  isPaymentBankOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeCartId = useAppSelector(({ activeCartSlice }: IActiveCartSliceState) => activeCartSlice.cartId);
  const history = useHistory();
  useEffect(() => {
    if (!!cartGroupId) {
      dispatch(setActiveCart({ cartId: cartGroupId }));
    }
  }, [dispatch, cartGroupId]);

  return (
    <>
      <Switch>
        <Route path={routeConfig.dashboard.payments.cartGroup.editCartGroup}>
          {cartGroupId ? <EditCartGroup cartGroupId={cartGroupId} /> : null}
        </Route>
        <Route path={routeConfig.dashboard.payments.cartGroup.createCartGroup}>
          <CreateCartGroup />
        </Route>
        <Route path={routeConfig.dashboard.payments.cartGroup.editSharedCartGroup.clientId}>
          {cartGroupId && clientId ? <EditSharedCartGroup clientId={clientId} cartGroupId={cartGroupId} /> : null}
        </Route>
        <Route path={routeConfig.dashboard.payments.cartGroup.shareCartGroup}>
          {cartGroupId ? <ShareCartGroup cartGroupId={cartGroupId} /> : null}
        </Route>
        <Route path={routeConfig.dashboard.payments.cartGroup.overview}>
          {cartGroupId ? <CartGroupOverview cartGroupId={cartGroupId} /> : null}
        </Route>
        <Route path={routeConfig.dashboard.payments.cartGroup.shareCartGroupSuccess}>
          {cartGroupId ? <ShareCartGroupSuccess cartGroupId={cartGroupId} /> : null}
        </Route>
        <Route path={routeConfig.dashboard.payments.cartGroup.editSharedCartGroupSuccess}>
          {cartGroupId ? <EditSharedCartGroupSuccess cartGroupId={cartGroupId} /> : null}
        </Route>
        <Route path={routeConfig.dashboard.payments.cartGroup.sharedClients}>
          {cartGroupId ? <CartGroupSharedClients cartGroupId={cartGroupId} /> : null}
        </Route>
        <Route exact path={routeConfig.dashboard.payments.cartGroup.self}>
          <CartGroup cartGroupId={activeCartId} previousMonthPaymentEnabled />
        </Route>
        <Route exact path={routeConfig.dashboard.payments.orderProcessing.self}>
          {orderId ? <OrderProcessing orderId={orderId} /> : null}
        </Route>
        <Route path={routeConfig.dashboard.payments.repeatPayment.self}>
          {serviceId && (
            <FormWrapper>
              <StyledPageWrapper noJustify>
                <Title>{t("payments.repeat_payment")}</Title>
                <ServiceRepeatPaymentForm id={serviceId} />
              </StyledPageWrapper>
            </FormWrapper>
          )}
        </Route>
        <Route path={routeConfig.dashboard.payments.addService.recurringPayments.self}>
          <FormWrapper>
            <StyledPageWrapper noJustify>
              <Title> {t("payments.new_payment")}</Title>
              {serviceId ? (
                <ServiceForm
                  id={serviceId}
                  ignoreGroup={ignoreGroup}
                  redirectOnServiceGroupSelect={getRecurrentPaymentRoute}
                  isPaymentBankOnly
                />
              ) : (
                <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "center" } }}>
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "485px", lg: "850px" },
                    }}
                  >
                    <ServiceListAutocomplete
                      autofocus
                      divided
                      initialValue={providers}
                      pathToPush={getRecurrentPaymentRoute}
                    />
                  </Box>
                </Box>
              )}
              <Button
                type="button"
                disableElevation
                variant="contained"
                onClick={() => {
                  history.push(reverse(routeConfig.dashboard.services.recurringPayments.addRecurringPayment.self));
                }}
              >
                {t("payments.returnBackToPayments")}
              </Button>
            </StyledPageWrapper>
          </FormWrapper>
        </Route>
        <Route path={routeConfig.dashboard.payments.addService.template.self}>
          <FormWrapper>
            <StyledPageWrapper noJustify>
              <Title> {t("payments.new_payment")}</Title>
              {serviceId ? (
                <ServiceForm
                  id={serviceId}
                  ignoreGroup={ignoreGroup}
                  templateId={templateId}
                  redirectOnServiceGroupSelect={(activeServiceId) =>
                    getTemplateServiceRoute(activeServiceId, templateId)
                  }
                />
              ) : (
                <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "center" } }}>
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "485px", lg: "850px" },
                    }}
                  >
                    <ServiceListAutocomplete
                      autofocus
                      divided
                      initialValue={providers}
                      pathToPush={(activeServiceId) => getTemplateServiceRoute(activeServiceId, templateId)}
                    />
                  </Box>
                </Box>
              )}
              <Button
                type="button"
                disableElevation
                variant="contained"
                onClick={() => {
                  if (templateId) {
                    history.push(
                      reverse(routeConfig.dashboard.services.templates.editTemplate.templateId, {
                        templateId,
                      })
                    );
                  } else {
                    history.push(reverse(routeConfig.dashboard.services.templates.addTemplate.self));
                  }
                }}
              >
                {t("payments.returnBackToRecurringPayments")}
              </Button>
            </StyledPageWrapper>
          </FormWrapper>
        </Route>
        <Route path={routeConfig.dashboard.payments.addService.self}>
          <FormWrapper>
            <StyledPageWrapper noJustify>
              <Title> {t("payments.new_payment")}</Title>
              {serviceId ? (
                <ServiceForm id={serviceId} ignoreGroup={ignoreGroup} />
              ) : (
                <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "center" } }}>
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "485px", lg: "850px" },
                    }}
                  >
                    <ServiceListAutocomplete autofocus divided initialValue={providers} />
                  </Box>
                </Box>
              )}
            </StyledPageWrapper>
          </FormWrapper>
        </Route>
        <Route path={routeConfig.dashboard.payments.cart.cartId}>
          {cartId ? <EditCart cartId={cartId} key={cartId} /> : null}
        </Route>
        <Route exact path={routeConfig.dashboard.payments.paymentOverview}>
          <PaymentOverview isPaymentBankOnly={isPaymentBankOnly} />
        </Route>
        <Route exact path={routeConfig.dashboard.order.cash}>
          {orderId ? <CashPayment orderId={orderId} /> : null}
        </Route>
        <Route exact path={routeConfig.dashboard.order.mip}>
          <BankPayment />
        </Route>
        {activeCartId && (
          <Route
            render={() => (
              <Redirect
                to={reverse(routeConfig.dashboard.payments.cartGroup.self, {
                  cartGroupId: activeCartId,
                })}
              />
            )}
          ></Route>
        )}
      </Switch>
    </>
  );
};
